import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { cssVar } from '@going-once/tokens';
import { Icon } from './icons';
import { Menu, type MenuGroup } from './menu';
import { Avatar, focusRing, Text, visuallyHidden } from './primitives';

/**
 * A data table: a real `<table>`, so a screen reader announces rows, columns and the
 * header of each cell, with a caption naming it and `scope="col"` on every header.
 *
 * Drawn as the design system's `DataTable`: the page's own surface in a ruled box, caps
 * headers, rows centred on their tallest cell. Wide content scrolls inside the table's
 * own box rather than pushing the page sideways: the admin frame's main column never
 * scrolls horizontally.
 *
 * **A row that opens** (`onRowOpen`) opens from a press anywhere on it, as the design
 * system's rows do — and, for a keyboard, from a button in its row header, which is the
 * one tab stop the row adds. Controls inside a row (the ••• menu) keep their own presses.
 */
export interface TableColumn<Row> {
  key: string;
  header: string;
  cell: (row: Row) => ReactNode;
  /** Keep the cell on one line: a timestamp or an id, which wrapping makes harder to read. */
  nowrap?: boolean;
  /** The column that names its row: drawn as `<th scope="row">`, so each cell is announced with it. */
  rowHeader?: boolean;
  /**
   * The order the rows already come in, by this column. It says so (`aria-sort` and an
   * arrow) and does nothing else: **the table never sorts** — a table that pages from a
   * server cannot re-sort itself, and one that can is the caller's call to make.
   */
  sorted?: 'descending' | 'ascending';
  /**
   * The header becomes a button that asks the caller to sort by this column. The caller
   * re-orders the rows and says how in `sorted`; leave this out where sorting would lie.
   */
  onSort?: () => void;
  /** Money and counts: right-aligned, tabular figures, so digits line up down the column. */
  numeric?: boolean;
  /** How wide the column is, or at least is, on the four-step column scale. */
  width?: ColumnWidth;
  minWidth?: ColumnWidth;
  /** The header is for a screen reader only — the actions column's "Actions". */
  hideHeader?: boolean;
}

/** `size.column.*`: 64 for a ••• cell, then 136, 160 and 220. */
export type ColumnWidth = 'actions' | 'sm' | 'md' | 'lg';

/** A press that belongs to a control inside the row, not to the row itself. */
const CONTROL = 'a, button, input, select, textarea, [role="menu"], [role="menuitem"]';

export function Table<Row>({
  caption,
  hideCaption,
  columns,
  rows,
  rowKey,
  onRowOpen,
  selectedKey,
  focusKey,
  empty,
}: {
  caption: string;
  /** The page already says what the table is (the design draws no caption), so only a screen reader hears it. */
  hideCaption?: boolean;
  columns: TableColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** The row is a way into its record. */
  onRowOpen?: ((row: Row) => void) | undefined;
  /** The row whose record is open: drawn selected and marked `aria-current`. */
  selectedKey?: string | undefined;
  /** Put focus on this row's open button when the table appears: the way back from the record it opened. */
  focusKey?: string | undefined;
  /** What the body says when there are no rows (an `EmptyState`), under the headers. */
  empty?: ReactNode;
}) {
  const [hovered, setHovered] = useState<string | undefined>(undefined);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusKey === undefined) return;
    for (const button of box.current?.querySelectorAll<HTMLElement>('[data-row-open]') ?? []) {
      if (button.dataset.rowOpen === focusKey) button.focus();
    }
  }, [focusKey]);
  // The padding every cell had before the row heights arrived, kept: a cell that wraps — the
  // audit log's action with its reason and ticket under it — needs room above and below, and
  // the heights are floors that only lift a short row, never a squeeze on a tall one.
  const cellPadding = `${cssVar('space.md')} ${cssVar('space.md')}`;
  const rule = `1px solid ${cssVar('color.border.default')}`;
  // The column whose cell carries the keyboard's way into the row.
  const opener = columns.find((column) => column.rowHeader)?.key ?? columns[0]?.key;
  return (
    <div
      ref={box}
      style={{
        // The containing block for the visually hidden header and caption: with none, they
        // anchor to the page and escape the scroll box, which widens the page.
        position: 'relative',
        overflowX: 'auto',
        borderRadius: cssVar('radius.md'),
        border: rule,
        background: cssVar('color.bg.base'),
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
        <caption style={hideCaption ? visuallyHidden : { captionSide: 'top', textAlign: 'left', padding: cellPadding }}>
          <Text step="label" tone="fg.muted">
            {caption}
          </Text>
        </caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                {...(column.sorted && { 'aria-sort': column.sorted })}
                style={{
                  height: cssVar('size.table.header'),
                  padding: cellPadding,
                  borderBottom: rule,
                  fontWeight: 'inherit',
                  whiteSpace: 'nowrap',
                  textTransform: 'uppercase',
                  textAlign: column.numeric ? 'right' : 'left',
                  ...widths(column),
                }}
              >
                <Header column={column} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && empty !== undefined ? (
            <tr>
              <td colSpan={columns.length}>{empty}</td>
            </tr>
          ) : null}
          {rows.map((row) => {
            const key = rowKey(row);
            const selected = key === selectedKey;
            const open = onRowOpen;
            return (
              <tr
                key={key}
                aria-current={selected ? 'true' : undefined}
                {...(open && {
                  onClick: (event: MouseEvent) => {
                    if ((event.target as Element).closest(CONTROL)) return;
                    open(row);
                  },
                  onPointerEnter: () => setHovered(key),
                  onPointerLeave: () => setHovered((current) => (current === key ? undefined : current)),
                })}
                style={{
                  cursor: open ? 'pointer' : 'default',
                  background: selected
                    ? cssVar('color.bg.raised')
                    : hovered === key
                      ? cssVar('color.neutral.tint')
                      : 'transparent',
                }}
              >
                {columns.map((column) => {
                  const Cell = column.rowHeader ? 'th' : 'td';
                  const content = column.cell(row);
                  return (
                    <Cell
                      key={column.key}
                      {...(column.rowHeader && { scope: 'row' })}
                      style={{
                        height: cssVar('size.table.row'),
                        padding: cellPadding,
                        borderBottom: rule,
                        verticalAlign: 'middle',
                        fontWeight: 'inherit',
                        textAlign: column.numeric ? 'right' : 'left',
                        ...(column.numeric ? { fontVariantNumeric: 'tabular-nums' } : {}),
                        ...(column.nowrap ? { whiteSpace: 'nowrap' } : { overflowWrap: 'anywhere' }),
                        ...widths(column),
                      }}
                    >
                      {open && column.key === opener ? (
                        <RowOpen rowKey={key} onPress={() => open(row)}>
                          {content}
                        </RowOpen>
                      ) : (
                        content
                      )}
                    </Cell>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const COLUMN = {
  actions: 'size.column.actions',
  sm: 'size.column.sm',
  md: 'size.column.md',
  lg: 'size.column.lg',
} as const;

function widths<Row>(column: TableColumn<Row>) {
  return {
    ...(column.width ? { width: cssVar(COLUMN[column.width]) } : {}),
    ...(column.minWidth ? { minWidth: cssVar(COLUMN[column.minWidth]) } : {}),
  };
}

function Header<Row>({ column }: { column: TableColumn<Row> }) {
  const [focused, setFocused] = useState(false);
  const label = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: cssVar('space.xs'),
        flexDirection: column.numeric ? 'row-reverse' : 'row',
        color: cssVar(column.sorted ? 'color.fg.default' : 'color.fg.muted'),
      }}
    >
      <Text step="label" tone={column.sorted ? 'fg.default' : 'fg.muted'}>
        {column.header}
      </Text>
      {column.sorted === 'descending' ? <Icon name="arrow-down" /> : null}
      {column.sorted === 'ascending' ? <Icon name="arrow-up" /> : null}
    </span>
  );
  if (column.hideHeader) return <span style={visuallyHidden}>{column.header}</span>;
  if (!column.onSort) return label;
  return (
    <button
      type="button"
      onClick={column.onSort}
      onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={{
        padding: 0,
        border: 'none',
        background: 'transparent',
        textTransform: 'inherit',
        cursor: 'pointer',
        ...(focused ? focusRing : {}),
      }}
    >
      {label}
    </button>
  );
}

/**
 * The row's way in, for a keyboard: its row header, as a button. Unstyled past the focus
 * ring, so the cell looks the same as its neighbours — the row's hover says it opens.
 */
function RowOpen({ rowKey, onPress, children }: { rowKey: string; onPress: () => void; children: ReactNode }) {
  const [focused, setFocused] = useState(false);
  return (
    <button
      type="button"
      data-row-open={rowKey}
      onClick={onPress}
      onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={{
        display: 'block',
        width: '100%',
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: 'inherit',
        font: 'inherit',
        textAlign: 'inherit',
        cursor: 'pointer',
        ...(focused ? focusRing : {}),
      }}
    >
      {children}
    </button>
  );
}

/**
 * A person or a store in a cell (the design system's `person` column): a row avatar —
 * their photo, else initials — beside their name and a muted line under it.
 */
export function PersonCell({
  name,
  sub,
  avatarUrl,
}: {
  name: string;
  sub?: string | undefined;
  avatarUrl?: string | undefined;
}) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: cssVar('space.md'), minWidth: 0 }}>
      <Avatar name={name} src={avatarUrl} />
      <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <Text truncate>{name}</Text>
        {sub ? (
          <Text step="label" tone="fg.muted" truncate>
            {sub}
          </Text>
        ) : null}
      </span>
    </span>
  );
}

/**
 * A row's ••• menu (the design system's `ActionsCell`). **Nothing at all when there are no
 * items** — not an empty menu behind a button — so a caller who may do nothing to this row
 * passes no groups and the cell is blank.
 */
export function ActionsCell({ label, groups }: { label: string; groups: readonly MenuGroup[] }) {
  return <Menu label={label} groups={groups} />;
}
